import { supabase } from '../lib/supabase';


/**
 * Test Account Service
 * Manages test account toggles and audit logging
 */

/**
 * Toggle test mode for a user (admin only)
 */
export async function toggleTestMode(userId, isTestAccount, reason = null) {
  try {
    const { data, error } = await supabase?.rpc('toggle_test_mode', {
      p_user_id: userId,
      p_is_test_account: isTestAccount,
      p_reason: reason
    });
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Toggle test mode error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Check if user is a test account
 */
export async function isTestAccount(userId) {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account')
      ?.eq('id', userId)
      ?.single();
    
    if (error) throw error;
    
    return { success: true, isTestAccount: data?.is_test_account || false };
  } catch (error) {
    console.error('Check test account error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get test mode audit logs
 */
export async function getTestModeAuditLogs(userId = null, limit = 100, offset = 0) {
  try {
    const { data, error } = await supabase?.rpc('get_test_mode_audit_logs', {
      p_user_id: userId,
      p_limit: limit,
      p_offset: offset
    });
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get test mode audit logs error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get all test accounts (admin only)
 */
export async function getAllTestAccounts() {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name, role, is_test_account, created_at')
      ?.eq('is_test_account', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get all test accounts error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get credit display for user (shows "Unlimited" for test accounts)
 */
export async function getCreditDisplay(userId) {
  try {
    const { data: userProfile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account')
      ?.eq('id', userId)
      ?.single();
    
    if (profileError) throw profileError;
    
    if (userProfile?.is_test_account) {
      return {
        success: true,
        isTestAccount: true,
        display: 'Unlimited (Test Mode)',
        credits: Infinity
      };
    }
    
    // Get actual credits for non-test accounts
    const { data: subscription, error: subError } = await supabase
      ?.from('user_subscriptions')
      ?.select('trial_credits, paid_credits, monthly_credits')
      ?.eq('user_id', userId)
      ?.single();
    
    if (subError) throw subError;
    
    const totalCredits = (subscription?.trial_credits || 0) + 
                        (subscription?.paid_credits || 0) + 
                        (subscription?.monthly_credits || 0);
    
    return {
      success: true,
      isTestAccount: false,
      display: totalCredits?.toString(),
      credits: totalCredits
    };
  } catch (error) {
    console.error('Get credit display error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get pending test account requests (admin only)
 */
export async function getPendingTestAccountRequests() {
  try {
    const { data, error } = await supabase?.rpc('get_pending_test_account_requests');
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get pending test account requests error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Approve test account request (admin only)
 */
export async function approveTestAccountRequest(userId, reason = null) {
  try {
    const { data, error } = await supabase?.rpc('approve_test_account_request', {
      p_user_id: userId,
      p_reason: reason
    });
    
    if (error) throw error;
    
    // Send approval email
    if (data?.user_email && data?.user_name) {
      // Get test code from user profile
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('test_account_code')
        ?.eq('id', userId)
        ?.single();

      // Import email service dynamically to avoid circular dependency
      const { sendTestAccountApprovalEmail } = await import('./emailService');
      await sendTestAccountApprovalEmail({
        userId,
        userEmail: data?.user_email,
        userName: data?.user_name,
        testCode: profile?.test_account_code || 'N/A',
        reason
      });
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Approve test account request error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Reject test account request (admin only)
 */
export async function rejectTestAccountRequest(userId, reason = null) {
  try {
    const { data, error } = await supabase?.rpc('reject_test_account_request', {
      p_user_id: userId,
      p_reason: reason
    });
    
    if (error) throw error;
    
    // Send rejection email
    if (data?.user_email && data?.user_name) {
      // Import email service dynamically to avoid circular dependency
      const { sendTestAccountRejectionEmail } = await import('./emailService');
      await sendTestAccountRejectionEmail({
        userId,
        userEmail: data?.user_email,
        userName: data?.user_name,
        reason
      });
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Reject test account request error:', error);
    return { success: false, error: error?.message };
  }
}